<template>
  <NavigationBar
    data-test-id="MtpNavigationBar"
    :dropdownMenuTitle="dropdownMenuTitle"
    :locationLabel="locationLabel"
    :logoImg="logoImg"
    :menuSections="menuSections"
    :showJcLogo="showJcLogo"
  />
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import Constants, {
  DashboardConsts,
  ItemTableConsts,
  ItemTypeConsts,
  SettingsConsts,
  StoreModuleMap,
  XhrConsts,
} from 'client/util/Constants';
import IntervalService from 'client/util/IntervalService';
import ModalEventBus from 'client/components/Modal/ModalEventBus';
import MspState from 'client/components/models/MspState';
import NavigationBar from 'client/components/NavigationBar/NavigationBar.vue';
import OrganizationsCollection from 'client/store/modules/collections/OrganizationsCollection';
import PageNavigationUtil from 'client/util/PageNavigationUtil';
import Util from 'client/util/Util';

const { urlOrgLogo, urlOrgName } = SettingsConsts;
const { xOrgIdKey } = XhrConsts;

export const organizationsCollectionName = 'MtpNavBarOrganizationsCollection';
const settingsModule = StoreModuleMap[ItemTypeConsts.settings];

export default {
  name: 'MtpNavigationBar',

  components: {
    NavigationBar,
  },

  props: {
    locationLabel: {
      type: String,
      default: DashboardConsts.locationLabel,
    },
    logoImg: {
      type: String,
      default: Constants.jcUndefinedLogo,
    },
    showJcLogo: Boolean,
  },

  data: () => ({
    dropdownMenuTitle: DashboardConsts.menuTitle,
    keepMenuFreshErrorCount: 0,
    keepMenuFreshInterval: null,
  }),

  IntervalService,
  MspState,
  Util,
  PageNavigationUtil,
  ModalEventBus,
  OrganizationsCollection,
  WindowOption: window,

  computed: {
    ...mapGetters(organizationsCollectionName, {
      organizations: 'getCollection',
    }),

    ...mapState(settingsModule, {
      currentOrgId: 'orgId',
    }),

    menuSections() {
      const menuItems = [
        {
          label: DashboardConsts.dashboardMenuItem,
          action: () => { this.navigateToMtp(); },
        },
      ];

      const organizationMenuItems = this.organizations
        .filter(organization => organization._id !== this.currentOrgId)
        .map((organization) => {
          const name = organization.displayName || Constants.orgNameNotSet;
          return {
            label: name,
            action: () => this.navigateToAdminPortalOrg(organization, name),
          };
        });

      if (organizationMenuItems.length > 0) {
        organizationMenuItems[0].addSeparator = true;
      }

      return [{ menuItems: [...menuItems, ...organizationMenuItems] }];
    },
  },

  created() {
    if (!(this.$store && this.$store.state && this.$store.state[organizationsCollectionName])) {
      this.$store.registerModule(
        organizationsCollectionName,
        this.$options.OrganizationsCollection,
      );
    }
    this.fetchCollection();
  },

  mounted() {
    this.startKeepMenuFresh();
  },

  destroyed() {
    this.cancelKeepMenuFresh();
  },

  methods: {
    ...mapActions(organizationsCollectionName, [
      'fetchCollection',
      'refreshCollection',
    ]),

    cancelKeepMenuFresh() {
      this.$options.IntervalService.clear(this.keepMenuFreshInterval);
      this.keepMenuFreshInterval = null;
    },

    handleKeepMenuFresh() {
      const handleRefreshError = () => {
        this.keepMenuFreshErrorCount += 1;

        if (this.keepMenuFreshErrorCount >= 3) this.cancelKeepMenuFresh();
      };

      this.refresh().catch(handleRefreshError.bind(this));
    },

    navigateToAdminPortalOrg(org, name) {
      const logo = org.logoUrl || Constants.jcUndefinedLogo;
      const orgId = org._id;
      const url = `/dashboard?${xOrgIdKey}=${orgId}&${urlOrgName}=${name}&${urlOrgLogo}=${logo}`;

      /**
       * Ideally we would want to use vue router to navigate between orgs,
       * but for some reason using the vue router equivalents of location.replace and
       * location.reload makes the page reload and stay on the same org.
       */
      this.$options.WindowOption.location.replace(`${window.location.origin}/#${url}`);
      this.$options.WindowOption.location.reload();
    },

    navigateToMtp() {
      this.$options.MspState.clearStorage();
      this.$options.PageNavigationUtil.setLocation('/mtp');
    },

    refresh() {
      return this.refreshCollection();
    },

    startKeepMenuFresh() {
      const { keepFreshInterval } = ItemTableConsts;
      this.cancelKeepMenuFresh();
      this.keepMenuFreshInterval = this.$options.IntervalService
        .set(() => this.handleKeepMenuFresh(), keepFreshInterval);
    },
  },
};
</script>
